// Iter-196d/e — Compact chip showing the resolved payment-delay for an
// invoice (in days). Clicking it opens a popover where the CEO can set or
// clear a per-invoice override. Read-only for non-CEO users.
//
// Re-used by:
//   • pages/Invoices.jsx (in the InvoiceDateStrip)
//   • pages/InvoiceRemindersAudit.jsx (next to each invoice header)
//
// Expects `inv` to carry `payment_delay_days_effective`, `payment_delay_source`
// and (optionally) `payment_delay_days` (the override). `onChanged` is called
// after a successful patch so the parent can reload.
import React, { useEffect, useState } from "react";
import { Clock, Star } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const SOURCE_LABEL = {
  invoice: "facture",
  mission: "mission",
  client: "client",
  default: "défaut 30j",
};

export default function PaymentDelayChip({ inv, onChanged }) {
  const { user } = useAuth();
  // Iter-240 — the delay is frozen once the invoice is paid.
  const canEdit = user?.role === "CEO" && !inv.paid_at;
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState("");
  const [saving, setSaving] = useState(false);

  const effective = inv.payment_delay_days_effective ?? (inv.payment_delay_days || 30);
  const isOverride = !!inv.payment_delay_days;
  const source = inv.payment_delay_source || "default";
  const sourceLabel = SOURCE_LABEL[source] || source;

  useEffect(() => { if (open) setVal(String(inv.payment_delay_days ?? "")); }, [open, inv.payment_delay_days]);

  const submit = async (newDelay) => {
    setSaving(true);
    try {
      await api.patch(`/invoices/${inv.invoice_id}/payment-delay`, { payment_delay_days: newDelay });
      toast.success(newDelay === null ? "Override supprimé" : `Délai mis à ${newDelay}j`);
      setOpen(false);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally { setSaving(false); }
  };

  const Pill = (
    <span
      data-testid={`invoice-delay-chip-${inv.invoice_id}`}
      data-source={source}
      title={canEdit
        ? `Délai de paiement : ${effective}j (source : ${sourceLabel}) — cliquer pour modifier`
        : inv.paid_at
          ? `Délai de paiement : ${effective}j — verrouillé (facture payée)`
          : `Délai de paiement : ${effective}j (source : ${sourceLabel})`}
      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border text-[9px] font-mono ${
        isOverride
          ? "bg-indigo-50 border-indigo-300 text-indigo-900 font-semibold"
          : "bg-zinc-50 border-zinc-200 text-zinc-600 hover:bg-zinc-100"
      } ${canEdit ? "cursor-pointer" : ""}`}
    >
      <Clock size={9} className="shrink-0" />
      <span>{effective}j</span>
      {isOverride && <Star size={8} className="shrink-0 fill-current" aria-label="override actif" />}
    </span>
  );

  if (!canEdit) return Pill;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="self-start"
          data-testid={`invoice-delay-chip-btn-${inv.invoice_id}`}
          onClick={(e) => e.stopPropagation()}
        >
          {Pill}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-72 p-3 space-y-3"
        align="start"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[11px] uppercase tracking-wider font-mono text-zinc-500 font-semibold">
          Délai de paiement
        </div>
        <div className="text-[11px] text-zinc-600">
          Délai effectif : <b>{effective} jours</b> · source : <b>{sourceLabel}</b>
        </div>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="text-[10px] uppercase tracking-wider font-mono text-zinc-500 mb-1 block">
              Override (jours)
            </label>
            <input
              type="number"
              min={1}
              max={365}
              value={val}
              onChange={(e) => setVal(e.target.value)}
              placeholder="ex. 60"
              data-testid={`invoice-delay-input-${inv.invoice_id}`}
              className="w-full h-8 px-2 text-sm border border-zinc-200 rounded"
            />
          </div>
          <button
            type="button"
            onClick={() => {
              const n = Number(val);
              if (!Number.isFinite(n) || n < 1 || n > 365) {
                toast.error("Entre 1 et 365 jours.");
                return;
              }
              submit(n);
            }}
            disabled={saving || !val}
            data-testid={`invoice-delay-save-${inv.invoice_id}`}
            className="px-3 h-8 text-xs bg-zinc-950 text-white rounded hover:bg-zinc-800 disabled:opacity-50"
          >
            {saving ? "…" : "Appliquer"}
          </button>
        </div>
        {isOverride && (
          <button
            type="button"
            onClick={() => submit(null)}
            disabled={saving}
            data-testid={`invoice-delay-clear-${inv.invoice_id}`}
            className="w-full px-3 h-8 text-xs border border-zinc-200 rounded hover:bg-zinc-50 text-zinc-700"
          >
            Supprimer l'override (revenir à la cascade)
          </button>
        )}
        <div className="text-[10px] text-zinc-400 leading-relaxed">
          La date d'échéance et les milestones de relance sont automatiquement recalculées à partir de la date d'envoi de la facture.
        </div>
      </PopoverContent>
    </Popover>
  );
}
